import cx from 'classnames';
import React from 'react';
import { Link } from 'react-router-dom';
import { CONTENT_ID } from '../../../lib/constants';
import { isValidUrl } from '../../../lib/util';
import Icon from '../Icon';
import Text from '../Text';
import styles from './Button.module.scss';
import usePresenter from './Button.presenter';
import getProps from './Button.props';
import customStyles from './Custom.module.scss';
import type { ButtonCombinedProps } from './types';

const Button: React.FC<ButtonCombinedProps> = (props) => {
  const {
    type,
    style,
    size,
    className,
    classes,
    disabled,
    buttonType,
    buttonForm,
    href,
    target,
    onClick,
    icon,
    text,
    // MANUAL OVERRIDE STARTS
    focusable = true,
    ariaLabel,
    ariaLabelledBy,
    // MANUAL OVERRIDE ENDS
  } = usePresenter(props);

  const variantName = `${type}${style}${size}`;
  const internalProps = getProps(variantName);
  const currentStyle = styles[`button${variantName}`];

  let componentView: JSX.Element | undefined;

  // MANUAL OVERRIDE STARTS
  let textView;
  let contentView;
  let iconView;
  // MANUAL OVERRIDE ENDS

  switch (variantName) {
    case 'IconTextMedium':
    case 'IconTextSmall': {
      iconView = (
        <Icon
          className={cx(styles.icon, classes?.icon)}
          {...internalProps.icon}
          {...icon} />
      );
      break;
    }
    case 'IconTextTextExtraSmall':
    case 'IconTextTextSmall': {
      contentView = (
        <div className={cx(styles.content, classes?.content)}>
          <Icon
            className={cx(styles.icon, classes?.icon)}
            {...internalProps.icon}
            {...icon} />
          <Text
            className={cx(styles.text, classes?.text)}
            {...internalProps.text}
            {...text} />
        </div>
      );
      break;
    }
    case 'TextIconTextMedium':
    case 'TextIconTextSmall':
    case 'TextIconTextUnderlineSmall': {
      contentView = (
        <div className={cx(styles.content, classes?.content)}>
          <Text
            className={cx(styles.text, classes?.text)}
            {...internalProps.text}
            {...text} />
          <Icon
            className={cx(styles.icon, classes?.icon)}
            {...internalProps.icon}
            {...icon} />
        </div>
      );
      break;
    }
    case 'TextContainedGreenLarge':
    case 'TextContainedGreenMedium':
    case 'TextContainedLarge':
    case 'TextContainedMedium':
    case 'TextContainedSmall': // MANUAL OVERRIDE
    case 'TextContainedExtraSmall': // MANUAL OVERRIDE
    case 'TextContainedWhiteMedium':
    case 'TextContainedWhiteLarge': // MANUAL OVERRIDE
    case 'TextOutlinePillMedium':
    case 'TextOutlineMedium':
    case 'TextOutlineSmall':
    case 'TextTextDarkMedium':
    case 'TextTextGreySmall':
    case 'TextTextMedium':
    case 'TextTextSmall':
    case 'TextTextLarge': // MANUAL OVERRIDE
    case 'TextTextUnderlineSmall': // MANUAL OVERRIDE
    case 'TextTextWhiteMedium':
    case 'TextTextWhiteSmall': {
      textView = (
        <Text
          className={cx(styles.text, classes?.text)}
          {...internalProps.text}
          {...text} />
      );
      break;
    }
    default:
      componentView = <div>�</div>;
      break;
  }

  if (href) {
    if (isValidUrl(href) || href === `#${CONTENT_ID}`) {
      return (
        <a
          target={target}
          rel='noreferrer'
          tabIndex={focusable ? 0 : -1}
          href={href}
          onClick={onClick}
          aria-label={ariaLabel}
          className={cx(currentStyle, customStyles.linkPath, className)}>
          {textView}
          {contentView}
          {iconView}
        </a>
      );
    }

    return (
      <Link
        tabIndex={focusable ? 0 : -1}
        to={href}
        onClick={onClick}
        aria-label={ariaLabel}
        data-item="true"
        className={cx(currentStyle, customStyles.linkPath, className)}>
        {textView}
        {contentView}
        {iconView}
      </Link>
    );
  }

  if (onClick || buttonForm) {
    return (
      <button
        tabIndex={focusable ? 0 : -1}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        type={buttonType}
        form={buttonForm}
        disabled={disabled}
        onClick={onClick}
        className={cx(currentStyle, className)}>
        {textView}
        {contentView}
        {iconView}
      </button>
    );
  }

  return componentView ? (
    <>
      {componentView}
    </>
  ) : (
    <div className={cx(currentStyle, className)}>
      {textView}
      {contentView}
      {iconView}
    </div>
  );
  // MANUAL OVERRIDE ENDS
};

export default Button;
